import React, { useMemo, useState, useEffect } from 'react';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Textarea } from './ui/textarea';
import { Label } from './ui/label';
import { Input } from './ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from './ui/dialog';
import { Popover, PopoverContent, PopoverTrigger } from './ui/popover';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from './ui/alert-dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from './ui/accordion';
import { Clock, MapPin, LogOut, User, Calendar, CheckCircle, Check, XCircle, MessageSquare, Send, Camera, FileText, Filter, Megaphone, ClipboardList, Plus, Edit, Save, Download } from 'lucide-react';
import CameraCapture from './CameraCapture';
import FieldServiceReportModal from './FieldServiceReportModal';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from './ui/table';
import { formatTaipeiTime as fmtTaipeiTime, formatTaipeiDate as fmtTaipeiDate, normalizeTs, toTaipeiISODate, TAIPEI_TIME_ZONE } from '../lib/time';
import SavedMfPayslipView, { computeMfDisplay } from './payroll/Shared/SavedMfPayslipView';
import SavedPayrollPayslipView, { computeSavedPayrollDisplayValues } from './payroll/Shared/SavedPayrollPayslipView';

const parseOutcomeTimeValue = (value) => {
  const raw = String(value || '').trim();
  const match = raw.match(/^(\d{1,2})\s*:\s*(\d{2})\s*(AM|PM)$/i);
  if (!match) {
    return { hour: '', minute: '00', period: 'AM' };
  }

  const hour = String(Number(match[1]));
  const minute = String(Number(match[2])).padStart(2, '0');
  const period = String(match[3] || 'AM').toUpperCase() === 'PM' ? 'PM' : 'AM';

  if (Number(hour) < 1 || Number(hour) > 12 || Number(minute) < 0 || Number(minute) > 59) {
    return { hour: '', minute: '00', period: 'AM' };
  }

  return { hour, minute, period };
};

const buildOutcomeTimeValue = ({ hour, minute, period }) => {
  const trimmedHour = String(hour || '').trim();
  if (!trimmedHour) {
    return '';
  }

  const safeMinute = String(minute || '00').padStart(2, '0');
  const safePeriod = String(period || 'AM').toUpperCase() === 'PM' ? 'PM' : 'AM';
  return `${trimmedHour}:${safeMinute} ${safePeriod}`;
};

const createEmptyOutcomeForm = () => ({
  startTimeHour: '',
  startTimeMinute: '00',
  startTimePeriod: 'AM',
  endTimeHour: '',
  endTimeMinute: '00',
  endTimePeriod: 'AM',
  contactPerson: '',
  position: '',
  contactNumber: '',
  result: '',
});

const EmployeeDashboard = ({ user, onLogout, axiosInstance }) => {
  const [currentTime, setCurrentTime] = useState(new Date());
  const [location, setLocation] = useState(null);
  const [isLoadingLocation, setIsLoadingLocation] = useState(false);
  const [todayAttendance, setTodayAttendance] = useState([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [comment, setComment] = useState('');
  const [schedule, setSchedule] = useState(null);
  const [announcements, setAnnouncements] = useState([]);
  const [isAnnouncementModalOpen, setIsAnnouncementModalOpen] = useState(false);
  const [isCameraOpen, setIsCameraOpen] = useState(false);
  const [pendingAttendanceType, setPendingAttendanceType] = useState(null);
  
  // Attendance History State
  const [isLogsModalOpen, setIsLogsModalOpen] = useState(false);
  const [attendanceHistory, setAttendanceHistory] = useState([]);
  const [isLoadingHistory, setIsLoadingHistory] = useState(false);
  const [logStartDate, setLogStartDate] = useState('');
  const [logEndDate, setLogEndDate] = useState('');

  // Field Service Report State
  const [isFSRModalOpen, setIsFSRModalOpen] = useState(false);
  const [isFSRListModalOpen, setIsFSRListModalOpen] = useState(false);
  const [fsrList, setFsrList] = useState([]);
  const [isLoadingFSR, setIsLoadingFSR] = useState(false);
  const [selectedFSR, setSelectedFSR] = useState(null);
  const [pendingFSRCount, setPendingFSRCount] = useState(0);
  const [isConfirmCompleteOpen, setIsConfirmCompleteOpen] = useState(false);
  const [reportToComplete, setReportToComplete] = useState(null);
  
  // Compliance Modal State
  const [isComplianceModalOpen, setIsComplianceModalOpen] = useState(false);
  const [complianceError, setComplianceError] = useState('');

  // Photo Warning Modal State
  const [isPhotoWarningOpen, setIsPhotoWarningOpen] = useState(false);
  const [isActionNotRecordedOpen, setIsActionNotRecordedOpen] = useState(false);

  // Daily Report State
  const [isDailyReportModalOpen, setIsDailyReportModalOpen] = useState(false);
  const [dailyReportDate, setDailyReportDate] = useState(toTaipeiISODate(new Date()));
  const [dailyReportData, setDailyReportData] = useState([]);
  const [isLoadingReport, setIsLoadingReport] = useState(false);
  const [reportRemarks, setReportRemarks] = useState({});
  const [pendingLocation, setPendingLocation] = useState(null);

  // Payslip State
  const [isPayslipModalOpen, setIsPayslipModalOpen] = useState(false);
  const [payslipTab, setPayslipTab] = useState('payroll');
  const [isLoadingPayslips, setIsLoadingPayslips] = useState(false);
  const [payslipRecordsBySource, setPayslipRecordsBySource] = useState({ payroll: [], mf: [] });
  const [payslipEnabled, setPayslipEnabled] = useState(false);
  const [payslipVisiblePeriodsBySource, setPayslipVisiblePeriodsBySource] = useState({ payroll: [], mf: [] });

  const [isItineraryEnrolled, setIsItineraryEnrolled] = useState(false);
  const [isItineraryModalOpen, setIsItineraryModalOpen] = useState(false);
  const [isLoadingItineraryToday, setIsLoadingItineraryToday] = useState(false);
  const [itineraryTodayItems, setItineraryTodayItems] = useState([]);
  const [activeItineraryRecordId, setActiveItineraryRecordId] = useState('');
  const [taskModalType, setTaskModalType] = useState(null);
  const [isTaskModalOpen, setIsTaskModalOpen] = useState(false);
  const [taskDraftItems, setTaskDraftItems] = useState([]);
  const [isSavingTasks, setIsSavingTasks] = useState(false);
  const [isSavingItineraryStatus, setIsSavingItineraryStatus] = useState(false);
  const [isStatusConfirmOpen, setIsStatusConfirmOpen] = useState(false);
  const [statusConfirmType, setStatusConfirmType] = useState(null);
  const [pendingStatusPayload, setPendingStatusPayload] = useState(null);
  const [isCancellationModalOpen, setIsCancellationModalOpen] = useState(false);
  const [cancellationDraft, setCancellationDraft] = useState('');
  const [cancellationError, setCancellationError] = useState('');
  const [isOutcomeModalOpen, setIsOutcomeModalOpen] = useState(false);
  const [outcomeForm, setOutcomeForm] = useState(() => createEmptyOutcomeForm());
  const [outcomeError, setOutcomeError] = useState('');
  const [isOutcomeValidationDialogOpen, setIsOutcomeValidationDialogOpen] = useState(false);
  const [isOutcomeStartTimePickerOpen, setIsOutcomeStartTimePickerOpen] = useState(false);
  const [isOutcomeEndTimePickerOpen, setIsOutcomeEndTimePickerOpen] = useState(false);

  const buildPayslipPeriodKey = (p) => {
    const period = String(p?.payroll_period || '').trim();
    const start = String(p?.start_date || '').trim();
    const end = String(p?.end_date || '').trim();
    return `${period}|${start}|${end}`;
  };

  const visiblePayrollKeySet = useMemo(() => {
    const list = Array.isArray(payslipVisiblePeriodsBySource?.payroll) ? payslipVisiblePeriodsBySource.payroll : [];
    return new Set(list.map(buildPayslipPeriodKey));
  }, [payslipVisiblePeriodsBySource]);

  const visibleMfKeySet = useMemo(() => {
    const list = Array.isArray(payslipVisiblePeriodsBySource?.mf) ? payslipVisiblePeriodsBySource.mf : [];
    return new Set(list.map(buildPayslipPeriodKey));
  }, [payslipVisiblePeriodsBySource]);

  const formatHM12 = (hm) => {
    if (!hm || typeof hm !== 'string' || !hm.includes(':')) return hm || 'N/A';
    const m = hm.trim().match(/^(\d{1,2}):(\d{2})(?:\s*(AM|PM))?$/i);
    if (!m) return hm;
    let h = Number(m[1]);
    const mm = m[2];
    let ap = m[3];
    if (!ap) {
      ap = h >= 12 ? 'PM' : 'AM';
      h = ((h % 12) === 0) ? 12 : (h % 12);
    } else {
      ap = ap.toUpperCase();
      h = ((h % 12) === 0) ? 12 : (h % 12);
    }
    return `${String(h).padStart(2, '0')}:${mm} ${ap}`;
  };

  useEffect(() => {
    const tick = () => setCurrentTime(new Date());
    tick();
    const timer = setInterval(tick, 1000);
    return () => clearInterval(timer);
  }, []);

  useEffect(() => {
    const run = async () => {
      try {
        if (!axiosInstance || !user?.id) return;
        const response = await axiosInstance.get('/app/settings');
        setPayslipEnabled(Boolean(response?.data?.payslip_enabled));
        setPayslipVisiblePeriodsBySource({
          payroll: Array.isArray(response?.data?.payslip_visible_payroll_periods)
            ? response.data.payslip_visible_payroll_periods
            : [],
          mf: Array.isArray(response?.data?.payslip_visible_mf_periods)
            ? response.data.payslip_visible_mf_periods
            : []
        });
      } catch {
        setPayslipEnabled(false);
        setPayslipVisiblePeriodsBySource({ payroll: [], mf: [] });
      }
    };
    run();
  }, [axiosInstance, user?.id]);

  useEffect(() => {
    const run = async () => {
      try {
        if (!axiosInstance || !user?.id) return;
        const response = await axiosInstance.get('/employee/itineraries/enrollment');
        setIsItineraryEnrolled(Boolean(response?.data?.enrolled));
      } catch {
        setIsItineraryEnrolled(false);
      }
    };
    run();
  }, [axiosInstance, user?.id]);

  useEffect(() => {
    fetchTodayAttendance();
    fetchMySchedule();
    fetchAnnouncements();
  }, [user]); // Re-fetch data when user changes (login/logout)

  // Reset component state when user changes (fresh login)
  useEffect(() => {
    if (user) {
      setTodayAttendance([]);
      setSchedule(null);
      setComment('');
      setLocation(null);
      setIsSubmitting(false);
      setIsLoadingLocation(false);
      setIsActionNotRecordedOpen(false);
      setPayslipRecordsBySource({ payroll: [], mf: [] });
      setIsPayslipModalOpen(false);
      setPayslipTab('payroll');
      setPayslipVisiblePeriodsBySource({ payroll: [], mf: [] });
      setIsItineraryModalOpen(false);
      setIsItineraryEnrolled(false);
      setItineraryTodayItems([]);
      setActiveItineraryRecordId('');
      setTaskModalType(null);
      setIsTaskModalOpen(false);
      setTaskDraftItems([]);
      setIsSavingTasks(false);
      setIsOutcomeModalOpen(false);
      setOutcomeForm(createEmptyOutcomeForm());
      setOutcomeError('');
      setIsOutcomeValidationDialogOpen(false);
      setIsOutcomeStartTimePickerOpen(false);
      setIsOutcomeEndTimePickerOpen(false);
    }
  }, [user?.id]); // Reset state when user ID changes

  const fetchTodayAttendance = async () => {
    try {
      const response = await axiosInstance.get('/attendance/today');
      const records = response.data.records || [];
      setTodayAttendance(records);
      console.log('Fetched today attendance:', records.length, 'records');
    } catch (error) {
      console.error('Error fetching attendance:', error);
      setTodayAttendance([]); // Reset to empty array on error
    }
  };

  const fetchMySchedule = async () => {
    try {
      const response = await axiosInstance.get('/schedule/my-schedule');
      setSchedule(response.data);
    } catch (error) {
      console.error('Error fetching schedule:', error);
      setSchedule(null);
    }
  };

  const fetchAnnouncements = async () => {
    try {
      if (!axiosInstance) {
        setAnnouncements([]);
        return [];
      }
      console.log('Fetching announcements...');
      const response = await axiosInstance.get('/marketing/announcements');
      const allAnnouncements = response.data;
      console.log('All announcements fetched:', allAnnouncements);
      
      const activeAnnouncements = allAnnouncements.filter(a => {
        if (!a.duration) return false;
        // Parse date reliably
        const durationDate = new Date(a.duration);
        // Set to end of day
        durationDate.setHours(23, 59, 59, 999);
        const now = new Date();
        const isActive = now <= durationDate;
        console.log(`Announcement "${a.title}": duration=${a.duration}, parsed=${durationDate}, now=${now}, active=${isActive}`);
        return isActive;
      });

      setAnnouncements(activeAnnouncements);
      console.log(
        activeAnnouncements.length > 0
          ? `Loaded ${activeAnnouncements.length} active announcements`
          : 'No active announcements found'
      );
      return activeAnnouncements;
    } catch (error) {
      console.error('Error fetching announcements:', error);
      setAnnouncements([]);
      return [];
    }
  };

  const fetchAttendanceHistory = async () => {
    setIsLoadingHistory(true);
    try {
      let url = '/attendance/my-history';
      const params = [];
      
      if (logStartDate) {
        params.push(`start_date=${logStartDate}`);
      }
      if (logEndDate) {
        params.push(`end_date=${logEndDate}`);
      }
      
      if (params.length > 0) {
        url += '?' + params.join('&');
      }
      
      const response = await axiosInstance.get(url);
      const rawRecords = response.data.records || [];

      // Process raw records into daily summaries
      const dailyGroups = {};
      
      rawRecords.forEach(record => {
        if (!record.timestamp) return;
        
        // Helper to get Taipei date key (YYYY-MM-DD)
        // Re-implementing logic here to ensure availability within this scope
        let dateStr = record.timestamp;
        if (typeof dateStr === 'string' && !dateStr.endsWith('Z') && !/[+-]\d{2}:?\d{2}$/.test(dateStr)) {
          dateStr += 'Z';
        }
        const date = new Date(dateStr);
        // Add 8 hours for Taipei to get correct day
        const taipeiDate = new Date(date.getTime() + (8 * 60 * 60 * 1000));
        const dateKey = taipeiDate.toISOString().split('T')[0];
        
        if (!dailyGroups[dateKey]) {
          dailyGroups[dateKey] = {
            date: record.timestamp, // Keep one timestamp for the date column
            logs: []
          };
        }
        dailyGroups[dateKey].logs.push(record);
      });

      const processedRecords = Object.values(dailyGroups).map(group => {
        // Sort logs by time
        const sortedLogs = group.logs.sort((a, b) => new Date(a.timestamp) - new Date(b.timestamp));
        
        const timeIn = sortedLogs.find(r => r.type === 'time_in');
        const timeOut = [...sortedLogs].reverse().find(r => r.type === 'time_out');
        
        let workHours = null;
        if (timeIn && timeOut) {
          // Calculate work hours
          let startStr = timeIn.timestamp;
          let endStr = timeOut.timestamp;
          // Normalize
          if (typeof startStr === 'string' && !startStr.endsWith('Z') && !/[+-]\d{2}:?\d{2}$/.test(startStr)) startStr += 'Z';
          if (typeof endStr === 'string' && !endStr.endsWith('Z') && !/[+-]\d{2}:?\d{2}$/.test(endStr)) endStr += 'Z';

          const start = new Date(startStr);
          const end = new Date(endStr);
          
          if (end > start) {
            const diff = (end - start) / (1000 * 60 * 60);
            workHours = diff.toFixed(2);
          }
        }

        return {
          id: group.date,
          date: group.date,
          time_in: timeIn ? timeIn.timestamp : null,
          time_out: timeOut ? timeOut.timestamp : null,
          status: timeIn?.status || 'Present',
          work_hours: workHours,
          location: timeIn?.location?.address || timeOut?.location?.address || 'Unknown'
        };
      }).sort((a, b) => new Date(b.date) - new Date(a.date));

      console.log('Processed daily records:', processedRecords.length);
      setAttendanceHistory(processedRecords);
      toast.success(`Loaded ${processedRecords.length} daily records`);
    } catch (error) {
      console.error('Error fetching attendance history:', error);
      toast.error('Failed to load attendance history');
      setAttendanceHistory([]);
    } finally {
      setIsLoadingHistory(false);
    }
  };

  const fetchFieldServiceReports = async () => {
    setIsLoadingFSR(true);
    try {
      const response = await axiosInstance.get('/field-service-reports');
      // Filter reports for current user if needed, or backend should handle it
      // Assuming backend returns all reports, we might want to filter or show all
      // For now showing all as per requirement context, but usually it's user specific
      // Let's assume the backend filters or we show all for the technician's branch
      const allReports = response.data || [];
      // Filter out 'Completed' reports as requested
      const activeReports = allReports.filter(report => report.status !== 'Completed');
      setFsrList(activeReports);
    } catch (error) {
      console.error('Error fetching FSRs:', error);
      toast.error('Failed to load Field Service Reports');
    } finally {
      setIsLoadingFSR(false);
    }
  };

  const handleMarkAsCompleted = (report) => {
    setReportToComplete(report);
    setIsConfirmCompleteOpen(true);
  };

  const handleConfirmCompletion = async () => {
    if (!reportToComplete) return;

    try {
      await axiosInstance.put(`/field-service-reports/${reportToComplete.id}`, {
        status: 'Completed'
      });
      
      toast.success('Report marked as completed');
      
      // Update local state to remove the item
      setFsrList(prevList => prevList.filter(item => item.id !== reportToComplete.id));
      
      // Update the badge count
      fetchPendingFSRCount();
      
      // Close dialog and clear state
      setIsConfirmCompleteOpen(false);
      setReportToComplete(null);
      
    } catch (error) {
      console.error('Error updating FSR status:', error);
      toast.error('Failed to update report status');
    }
  };

  const fetchPendingFSRCount = async () => {
    if (user.branch !== 'Technical') return;
    try {
      const response = await axiosInstance.get('/field-service-reports/pending-count');
      setPendingFSRCount(response.data.count);
    } catch (error) {
      console.error('Error fetching pending FSR count:', error);
    }
  };

  useEffect(() => {
    if (user && user.branch === 'Technical') {
      fetchPendingFSRCount();
    }
  }, [user]);

  const handleOpenFSRList = () => {
    setIsFSRListModalOpen(true);
    fetchFieldServiceReports();
    // Refresh count when opening list
    fetchPendingFSRCount();
  };

  const handleCreateNewFSR = () => {
    setSelectedFSR(null);
    setIsFSRModalOpen(true);
  };

  const handleEditFSR = (report) => {
    setSelectedFSR(report);
    setIsFSRModalOpen(true);
  };

  const handleFSRModalClose = () => {
    setIsFSRModalOpen(false);
    fetchFieldServiceReports(); // Refresh list after close (save/update)
  };

  const handleViewLogs = () => {
    setIsLogsModalOpen(true);
    fetchAttendanceHistory();
  };

  const activeItineraryItem = useMemo(() => {
    const list = Array.isArray(itineraryTodayItems) ? itineraryTodayItems : [];
    return list.find((item) => String(item?.id || '') === String(activeItineraryRecordId || '')) || null;
  }, [itineraryTodayItems, activeItineraryRecordId]);

  const pendingItineraryRecord = useMemo(() => {
    const recordId = String(pendingStatusPayload?.itinerary_record_id || '');
    if (!recordId) return null;
    const list = Array.isArray(itineraryTodayItems) ? itineraryTodayItems : [];
    return list.find((item) => String(item?.id || '') === recordId) || null;
  }, [itineraryTodayItems, pendingStatusPayload]);

  const fetchItineraryToday = async () => {
    if (!axiosInstance) return;
    setIsLoadingItineraryToday(true);
    try {
      const response = await axiosInstance.get('/employee/itineraries/today');
      const items = Array.isArray(response?.data) ? response.data : [];
      setItineraryTodayItems(items);
      setActiveItineraryRecordId((prev) => {
        const prevId = String(prev || '');
        if (prevId && items.some((item) => String(item?.id || '') === prevId)) return prevId;
        return String(items?.[0]?.id || '');
      });
    } catch (error) {
      console.error('Error fetching itineraries:', error);
      setItineraryTodayItems([]);
      setActiveItineraryRecordId('');
      toast.error('Failed to load itineraries');
    } finally {
      setIsLoadingItineraryToday(false);
    }
  };

  const handleOpenItineraryModal = () => {
    setIsItineraryModalOpen(true);
    fetchItineraryToday();
  };

  const handleOpenTaskModal = (type) => {
    if (!activeItineraryItem) return;
    const kind = type === 'priority' ? 'priority' : 'todo';
    const stateList = kind === 'priority' ? activeItineraryItem?.priority_items : activeItineraryItem?.todo_items_state;
    const baseList = kind === 'priority' ? activeItineraryItem?.priorities : activeItineraryItem?.todo_items;

    const hydrated = Array.isArray(stateList) && stateList.length > 0
      ? stateList
      : (Array.isArray(baseList) ? baseList.map((text, index) => ({ text, finished: false, position: index })) : []);

    setTaskModalType(kind);
    setTaskDraftItems(hydrated.map((item) => ({
      position: Number.isInteger(item?.position) ? item.position : null,
      text: String(item?.text || '').trim(),
      finished: Boolean(item?.finished),
    })).filter((item) => item.text));
    setIsTaskModalOpen(true);
  };

  const toggleTaskDraftItem = (index) => {
    setTaskDraftItems((prev) => prev.map((item, i) => (
      i === index ? { ...item, finished: !item.finished } : item
    )));
  };

  const handleSaveTaskUpdates = async () => {
    if (!axiosInstance || !activeItineraryItem?.id || !taskModalType) return;
    setIsSavingTasks(true);
    try {
      await axiosInstance.put('/employee/itineraries/tasks', {
        itinerary_record_id: activeItineraryItem.id,
        task_type: taskModalType,
        items: taskDraftItems,
      });

      setItineraryTodayItems((prev) => prev.map((item) => {
        if (String(item?.id || '') !== String(activeItineraryItem.id)) return item;
        if (taskModalType === 'priority') return { ...item, priority_items: taskDraftItems };
        return { ...item, todo_items_state: taskDraftItems };
      }));

      toast.success('Tasks updated');
      setIsTaskModalOpen(false);
    } catch (error) {
      console.error('Error updating tasks:', error);
      toast.error('Failed to update tasks');
    } finally {
      setIsSavingTasks(false);
    }
  };

  const handleOpenCancellationModal = () => {
    if (!activeItineraryItem?.id) return;
    setCancellationDraft(String(activeItineraryItem?.cancellation_reason || ''));
    setCancellationError('');
    setIsCancellationModalOpen(true);
  };

  const handleSaveCancellationReason = () => {
    if (!activeItineraryItem?.id) return;
    const trimmed = String(cancellationDraft || '').trim();
    if (!trimmed) {
      setCancellationError('Reason is required');
      return;
    }
    setIsCancellationModalOpen(false);
    setPendingStatusPayload({
      itinerary_record_id: String(activeItineraryItem.id),
      finished: false,
      cancelled: true,
      cancellation_reason: trimmed,
    });
    setStatusConfirmType('cancel');
    setIsStatusConfirmOpen(true);
  };

  const handleRequestFinishItinerary = () => {
    if (!activeItineraryItem?.id) return;
    const startTimeParts = parseOutcomeTimeValue(activeItineraryItem?.outcome_start_time);
    const endTimeParts = parseOutcomeTimeValue(activeItineraryItem?.outcome_end_time);
    setOutcomeForm({
      startTimeHour: startTimeParts.hour,
      startTimeMinute: startTimeParts.minute,
      startTimePeriod: startTimeParts.period,
      endTimeHour: endTimeParts.hour,
      endTimeMinute: endTimeParts.minute,
      endTimePeriod: endTimeParts.period,
      contactPerson: String(activeItineraryItem?.outcome_contact_person || ''),
      position: String(activeItineraryItem?.outcome_position || ''),
      contactNumber: String(activeItineraryItem?.outcome_contact_number || ''),
      result: String(activeItineraryItem?.itinerary_outcome || ''),
    });
    setOutcomeError('');
    setIsOutcomeModalOpen(true);
  };

  const updateOutcomeFormField = (field, value) => {
    setOutcomeForm((prev) => ({
      ...prev,
      [field]: field === 'contactNumber' ? String(value || '').replace(/\D+/g, '') : value,
    }));
  };

  const handleSaveItineraryOutcome = () => {
    if (!activeItineraryItem?.id) return;
    const nextPayload = {
      itinerary_record_id: String(activeItineraryItem.id),
      finished: true,
      cancelled: false,
      cancellation_reason: '',
      outcome_start_time: buildOutcomeTimeValue({
        hour: outcomeForm.startTimeHour,
        minute: outcomeForm.startTimeMinute,
        period: outcomeForm.startTimePeriod,
      }),
      outcome_end_time: buildOutcomeTimeValue({
        hour: outcomeForm.endTimeHour,
        minute: outcomeForm.endTimeMinute,
        period: outcomeForm.endTimePeriod,
      }),
      outcome_contact_person: String(outcomeForm.contactPerson || '').trim(),
      outcome_position: String(outcomeForm.position || '').trim(),
      outcome_contact_number: String(outcomeForm.contactNumber || '').trim(),
      itinerary_outcome: String(outcomeForm.result || '').trim(),
    };

    if (!Object.values(nextPayload).slice(4).every(Boolean)) {
      setOutcomeError('All fields should be filled up to save.');
      setIsOutcomeValidationDialogOpen(true);
      return;
    }

    setIsOutcomeModalOpen(false);
    setPendingStatusPayload(nextPayload);
    setStatusConfirmType('finish');
    setIsStatusConfirmOpen(true);
  };

  const handleConfirmStatusUpdate = async () => {
    if (!axiosInstance || !pendingStatusPayload?.itinerary_record_id) {
      setIsStatusConfirmOpen(false);
      setPendingStatusPayload(null);
      setStatusConfirmType(null);
      return;
    }

    const recordId = String(pendingStatusPayload.itinerary_record_id);

    setIsSavingItineraryStatus(true);
    try {
      await axiosInstance.put('/employee/itineraries/status', pendingStatusPayload);

      setItineraryTodayItems((prev) => prev.map((item) => (
        String(item?.id || '') === recordId
          ? {
              ...item,
              status_finished: Boolean(pendingStatusPayload.finished),
              status_cancelled: Boolean(pendingStatusPayload.cancelled),
              cancellation_reason: pendingStatusPayload.cancellation_reason || '',
              itinerary_outcome: pendingStatusPayload.itinerary_outcome || '',
              outcome_start_time: pendingStatusPayload.outcome_start_time || '',
              outcome_end_time: pendingStatusPayload.outcome_end_time || '',
              outcome_contact_person: pendingStatusPayload.outcome_contact_person || '',
              outcome_position: pendingStatusPayload.outcome_position || '',
              outcome_contact_number: pendingStatusPayload.outcome_contact_number || '',
            }
          : item
      )));

      toast.success('Status updated');
    } catch (error) {
      console.error('Error updating status:', error);
      toast.error('Failed to update status');
    } finally {
      setIsSavingItineraryStatus(false);
      setIsStatusConfirmOpen(false);
      setPendingStatusPayload(null);
      setStatusConfirmType(null);
      setIsOutcomeModalOpen(false);
      setOutcomeForm(createEmptyOutcomeForm());
      setOutcomeError('');
      setIsOutcomeValidationDialogOpen(false);
      setIsOutcomeStartTimePickerOpen(false);
      setIsOutcomeEndTimePickerOpen(false);
    }
  };

  const formatCurrency = (value) => {
    const num = typeof value === 'number' ? value : parseFloat(value);
    const safe = Number.isFinite(num) ? num : 0;
    return `${String.fromCharCode(0x20B1)}${safe.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  };

  const n = (value) => {
    if (value === null || value === undefined) return 0;
    if (typeof value === 'number') return Number.isFinite(value) ? value : 0;
    const parsed = parseFloat(String(value).replace(/,/g, '').trim());
    return Number.isFinite(parsed) ? parsed : 0;
  };
  const getMeaningfulText = (value) => {
    const text = String(value ?? '').trim();
    if (!text) return '';
    return text.toLowerCase() === 'n/a' ? '' : text;
  };

  // Removes UTF-8-as-Latin1 mojibake (e.g. "â€¢" for a bullet) and other unusual
  // characters so payslip fields never show garbled text below the employee name.
  const sanitizeDisplayText = (value) => {
    return String(value ?? '')
      .replace(/\u00e2\u0080\u00a2/g, ' ') // "â€¢" mojibake bullet
      .replace(/\u2022/g, ' ')             // literal bullet •
      .replace(/[\u0000-\u001f\u0080-\u009f\uFFFD]/g, '') // control / replacement chars
      .replace(/\s+/g, ' ')
      .trim();
  };

  const sanitizeFilenamePart = (value) => {
    return String(value || '')
      .replace(/[\\/:*?"<>|]+/g, '')
      .replace(/\s+/g, ' ')
      .trim();
  };

  const enrichSelfPayslipRecord = (record) => {
    const safeRecord = record || {};
    return {
      ...safeRecord,
      employee_name: sanitizeDisplayText(getMeaningfulText(safeRecord?.employee_name) || getMeaningfulText(user?.full_name)),
      employee_email: sanitizeDisplayText(getMeaningfulText(safeRecord?.employee_email) || getMeaningfulText(user?.email)),
      branch: sanitizeDisplayText(getMeaningfulText(safeRecord?.branch) || getMeaningfulText(user?.branch)),
      tin_number: sanitizeDisplayText(getMeaningfulText(safeRecord?.tin_number) || getMeaningfulText(user?.tin_number)),
      sss_number: sanitizeDisplayText(getMeaningfulText(safeRecord?.sss_number) || getMeaningfulText(user?.sss_number)),
      phic_number: sanitizeDisplayText(getMeaningfulText(safeRecord?.phic_number) || getMeaningfulText(user?.phic_number)),
      hdmf_number: sanitizeDisplayText(getMeaningfulText(safeRecord?.hdmf_number) || getMeaningfulText(user?.hdmf_number))
    };
  };

  const fetchMyPayslips = async () => {
    if (!axiosInstance) return;
    setIsLoadingPayslips(true);
    try {
      const [payrollResp, mfResp] = await Promise.all([
        axiosInstance.get('/payroll/my-records?source=payroll'),
        axiosInstance.get('/payroll/my-records?source=mf')
      ]);

      const payroll = (Array.isArray(payrollResp.data) ? payrollResp.data : []).map(enrichSelfPayslipRecord);
      const mf = (Array.isArray(mfResp.data) ? mfResp.data : []).map(enrichSelfPayslipRecord);
      setPayslipRecordsBySource({ payroll, mf });
    } catch (error) {
      console.error('Error fetching payslips:', error);
      toast.error('Failed to load payslips');
      setPayslipRecordsBySource({ payroll: [], mf: [] });
    } finally {
      setIsLoadingPayslips(false);
    }
  };

  useEffect(() => {
    if (!isPayslipModalOpen) return;
    fetchMyPayslips();
  }, [isPayslipModalOpen]);

  const handleViewPayslips = () => {
    setIsPayslipModalOpen(true);
  };

  const handleDownloadPayslip = async (record, source) => {
    try {
      const elementId = `payslip-content-${source}-${record.id}`;
      const element = document.getElementById(elementId);
      if (!element) {
        toast.error('Payslip content not found');
        return;
      }

      const [{ default: html2canvas }, { jsPDF }] = await Promise.all([
        import('html2canvas'),
        import('jspdf')
      ]);

      const employeeName = sanitizeFilenamePart(record.employee_name || record.employeeName || 'Employee');
      const start = sanitizeFilenamePart(record.start_date || '');
      const end = sanitizeFilenamePart(record.end_date || '');
      const filename = `Payslip-${employeeName}-${start}-${end}.pdf`;

      if (document?.fonts?.ready) {
        try {
          await document.fonts.ready;
        } catch {}
      }
      await new Promise((resolve) => setTimeout(resolve, 50));

      const canvas = await html2canvas(element, {
        scale: 2.5,
        useCORS: true,
        allowTaint: true,
        backgroundColor: '#ffffff',
        logging: false,
        windowWidth: element.scrollWidth,
        windowHeight: element.scrollHeight
      });

      const pdf = new jsPDF({
        orientation: 'portrait',
        unit: 'mm',
        format: 'a4',
        compress: true
      });
      const pageWidth = pdf.internal.pageSize.getWidth();
      const pageHeight = pdf.internal.pageSize.getHeight();
      const margin = 10;
      const contentWidth = pageWidth - (margin * 2);
      const renderedHeight = (canvas.height * contentWidth) / canvas.width;
      const pageContentHeight = pageHeight - (margin * 2);
      const imageData = canvas.toDataURL('image/png');

      let remainingHeight = renderedHeight;
      let yOffset = margin;

      pdf.addImage(imageData, 'PNG', margin, yOffset, contentWidth, renderedHeight, undefined, 'FAST');
      remainingHeight -= pageContentHeight;

      while (remainingHeight > 0) {
        pdf.addPage();
        yOffset = margin - (renderedHeight - remainingHeight);
        pdf.addImage(imageData, 'PNG', margin, yOffset, contentWidth, renderedHeight, undefined, 'FAST');
        remainingHeight -= pageContentHeight;
      }

      pdf.save(filename);
      toast.success('Payslip downloaded');
    } catch (error) {
      console.error('Error downloading payslip:', error);
      toast.error('Failed to download payslip');
    }
  };

  const renderPayslipAccordion = (source) => {
    const listRaw = payslipRecordsBySource?.[source];
    const list = Array.isArray(listRaw) ? listRaw : [];
    const visibleKeySet = source === 'mf' ? visibleMfKeySet : visiblePayrollKeySet;
    const listFiltered = visibleKeySet.size === 0
      ? []
      : list.filter((rec) => visibleKeySet.has(buildPayslipPeriodKey(rec)));

    if (isLoadingPayslips) {
      return <div className="py-8 text-center text-gray-500">Loading payslips...</div>;
    }

    if (listFiltered.length === 0) {
      return <div className="py-8 text-center text-gray-500">No payslips found.</div>;
    }

    return (
      <Accordion type="single" collapsible className="w-full border rounded-lg bg-white">
        {listFiltered.map((rec) => (
          (() => {
            const payrollDisplay = source === 'payroll'
              ? computeSavedPayrollDisplayValues({
                record: rec,
                displayedSss: n(rec?.sss_contribution),
                displayedTax: n(rec?.withholding_tax)
              })
              : null;
            const mfDisplay = source === 'mf' ? computeMfDisplay(rec) : null;

            return (
              <AccordionItem key={rec.id} value={rec.id} className="px-2">
                <AccordionTrigger className="px-2">
                  <div className="flex flex-1 items-center justify-between pr-4">
                    <div className="text-left">
                      <div className="font-semibold text-gray-900">
                        {rec.payroll_period || 'Payslip'}
                      </div>
                      <div className="text-xs text-gray-500">
                        {rec.start_date} to {rec.end_date}
                      </div>
                    </div>
                    <div className="text-right">
                      <div className="font-bold text-emerald-700">
                        {formatCurrency(source === 'mf' ? mfDisplay?.netMf : payrollDisplay?.displayNetPay)}
                      </div>
                      <div className="text-xs text-gray-500">Net Pay</div>
                    </div>
                  </div>
                </AccordionTrigger>
                <AccordionContent className="px-2">
                  <div className="flex justify-end">
                    <Button
                      onClick={() => handleDownloadPayslip(rec, source)}
                      className="bg-indigo-600 hover:bg-indigo-700 text-white flex items-center gap-2"
                    >
                      <Download className="h-4 w-4" />
                      Download PDF
                    </Button>
                  </div>

                  <div id={`payslip-content-${source}-${rec.id}`} className="bg-white p-4 border rounded-lg mt-3">
                    {source === 'mf' ? (
                      <SavedMfPayslipView record={rec} className="bg-white p-0 border-0 rounded-none" />
                    ) : (
                      <SavedPayrollPayslipView
                        record={rec}
                        displayedSss={payrollDisplay?.displayedSss}
                        displayedTax={payrollDisplay?.displayedTax}
                        displayTotalDeductions={payrollDisplay?.displayTotalDeductions}
                        displayNetPay={payrollDisplay?.displayNetPay}
                        loanSummary={payrollDisplay?.loanSummary}
                        className="bg-white p-0 border-0 rounded-none"
                      />
                    )}
                  </div>
                </AccordionContent>
              </AccordionItem>
            );
          })()
        ))}
      </Accordion>
    );
  };

  const handleDebugAttendance = async () => {
    try {
      const response = await axiosInstance.get('/attendance/my-history-debug');
      console.log('=== DEBUG ATTENDANCE INFO ===');
      console.log('Current User:', response.data.user_info);
      console.log('Total Records for Current User:', response.data.total_records_for_current_user);
      console.log('All User IDs in Attendance:', response.data.all_user_ids_in_attendance);
      console.log('Sample Records:', response.data.sample_records);
      console.log('============================');
      alert(`Debug Info:\nUser ID: ${response.data.user_info.current_user_id}\nTotal Records: ${response.data.total_records_for_current_user}\n\nCheck console for details`);
    } catch (error) {
      console.error('Debug error:', error);
    }
  };

  const handleApplyDateFilter = () => {
    if (logStartDate && logEndDate && logStartDate > logEndDate) {
      toast.error('Start date must be before end date');
      return;
    }
    fetchAttendanceHistory();
  };

  const handleClearDateFilter = () => {
    setLogStartDate('');
    setLogEndDate('');
    fetchAttendanceHistory();
  };

  const getCurrentLocation = () => {
    return new Promise((resolve, reject) => {
      if (!navigator.geolocation) {
        reject(new Error('Geolocation is not supported by this browser'));
        return;
      }
      try {
        const ua = (navigator.userAgent || '').toLowerCase();
        const isMobile = /android|webos|iphone|ipad|ipod|blackberry|iemobile|opera mini|mobile\s|mobi\b|phone/i.test(ua);
        const hasTouch = !!(navigator.maxTouchPoints && navigator.maxTouchPoints > 1);
        const isDesktop = !isMobile && !hasTouch;
        // Desktop: enableHighAccuracy=false because office PCs (Ethernet/no GPS)
        // otherwise block for 20+ seconds trying to wake a non-existent GPS chip.
        // Mobile (phone): true (high accuracy required for outdoor punch-in).
        // Cache: 5 min maxAge + 10s hard timeout; matches EmployeeLandingPage.
        const opts = {
          enableHighAccuracy: isDesktop ? false : true,
          timeout: 10000,
          maximumAge: 5 * 60 * 1000,
        };
        navigator.geolocation.getCurrentPosition(
          (position) => {
            const coords = {
              latitude: position.coords.latitude,
              longitude: position.coords.longitude,
              accuracy: position.coords.accuracy,
            };
            resolve(coords);
          },
          (error) => {
            reject(error);
          },
          opts,
        );
      } catch (err) {
        reject(err || new Error('Failed to start location lookup'));
      }
    });
  };

  const handleAttendance = async (type) => {
    setPendingAttendanceType(type);
    setIsCameraOpen(true);
  };

  const submitAttendance = async (photoBlob, rearPhotoBlob, locationData, type = pendingAttendanceType) => {
    setIsSubmitting(true);
    
    try {
      // Prepare form data for file upload
      const formData = new FormData();
      formData.append('type', type);
      if (locationData) {
        formData.append('location', JSON.stringify(locationData));
      }
      if (comment.trim()) {
        formData.append('comment', comment.trim());
      }
      
      // Add photo if captured
      if (photoBlob) {
        const timestamp = new Date().toISOString();
        const base = `attendance_${user.employee_id || user.id}_${timestamp}`;
        formData.append('photo', photoBlob, `${base}_FC.jpg`);
        if (rearPhotoBlob) {
          formData.append('photo', rearPhotoBlob, `${base}_RC.jpg`);
        }
      }

      // Submit attendance with photo
      await axiosInstance.post('/attendance', formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });

      // Clear comment after successful submission
      setComment('');

      // Refresh today's attendance
      await fetchTodayAttendance();
      
      const photoStatus = photoBlob ? (rearPhotoBlob ? 'with photos' : 'with photo') : 'without photo';
      toast.success(`${type === 'time_in' ? 'Time In' : 'Time Out'} recorded successfully ${photoStatus}!`);
      
      // Show announcements after successful Time In
      if (type === 'time_in') {
        const activeAnnouncements = await fetchAnnouncements();
        if (activeAnnouncements.length > 0) {
          setIsAnnouncementModalOpen(true);
        }
      }
      
    } catch (error) {
      console.error('Error recording attendance:', error);
      setIsActionNotRecordedOpen(true);
    } finally {
      setIsLoadingLocation(false);
      setIsSubmitting(false);
      setPendingAttendanceType(null);
      setIsCameraOpen(false);
      setIsPhotoWarningOpen(false);
      setPendingLocation(null);
    }
  };

  const handlePhotoCapture = async (photoBlob, rearPhotoBlob) => {
    // Capture the type immediately
    const typeToSubmit = pendingAttendanceType;
    
    setIsSubmitting(true);
    setIsLoadingLocation(true);
    
    try {
      // 1. Validate Location (Mandatory)
      let coords = null;
      try {
        coords = await getCurrentLocation();
        setLocation(coords);
      } catch (locErr) {
        console.warn('Location unavailable:', locErr);
        setComplianceError('Location is required. Please enable location services to proceed.');
        setIsComplianceModalOpen(true);
        setIsLoadingLocation(false);
        setIsSubmitting(false);
        setIsCameraOpen(false);
        return;
      }
      const locationData = coords ? {
        latitude: coords.latitude,
        longitude: coords.longitude,
        address: `Lat: ${coords.latitude.toFixed(6)}, Lng: ${coords.longitude.toFixed(6)}`
      } : undefined;

      // 2. Check Photo
      if (photoBlob) {
        await submitAttendance(photoBlob, rearPhotoBlob, locationData, typeToSubmit);
      } else {
        // No photo -> Show warning
        setPendingLocation(locationData);
        setIsPhotoWarningOpen(true);
        setIsCameraOpen(false);
        setIsLoadingLocation(false);
        setIsSubmitting(false);
      }

    } catch (error) {
      console.error('Error in attendance setup:', error);
      setIsLoadingLocation(false);
      setIsSubmitting(false);
    }
  };

  const handleProceedWithoutPhoto = () => {
    if (pendingAttendanceType && pendingLocation) {
      submitAttendance(null, null, pendingLocation, pendingAttendanceType);
    } else {
      toast.error('Session expired. Please try again.');
      setIsPhotoWarningOpen(false);
    }
  };

  const handleAddPhoto = () => {
    setIsPhotoWarningOpen(false);
    setIsCameraOpen(true);
  };

  const formatDateFromBackend = (timestamp) => {
    if (!timestamp) return '';
    return new Date(normalizeTs(timestamp)).toLocaleDateString('en-US', {
      timeZone: TAIPEI_TIME_ZONE,
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const formatTimestampFromBackend = (timestamp) => {
    if (!timestamp) return '';
    return fmtTaipeiTime(timestamp);
  };

  const formatTime = (date) => {
    return date.toLocaleTimeString('en-US', {
      hour12: true,
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit'
    });
  };

  const formatDate = (date) => {
    return date.toLocaleDateString('en-US', {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  // Format timestamp to Taipei time using timezone option
  const formatTaipeiDate = (timestamp) => fmtTaipeiDate(timestamp);

  const formatTaipeiTime = (timestamp) => fmtTaipeiTime(timestamp);

  const getLastTimeIn = () => {
    // Get the most recent time_in record (sort by timestamp descending)
    const timeInRecords = todayAttendance
      .filter(record => record.type === 'time_in')
      .sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp));
    return timeInRecords[0] || null;
  };

  const getLastTimeOut = () => {
    // Get the most recent time_out record (sort by timestamp descending)
    const timeOutRecords = todayAttendance
      .filter(record => record.type === 'time_out')
      .sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp));
    return timeOutRecords[0] || null;
  };

  const fetchDailyReport = async () => {
    setIsLoadingReport(true);
    try {
      // Fetch attendance history for the specific date
      const response = await axiosInstance.get('/attendance/my-history', {
        params: {
          start_date: dailyReportDate,
          end_date: dailyReportDate
        }
      });
      
      const records = response.data.records || [];
      
      // Sort records by timestamp
      const sortedRecords = [...records].sort((a, b) => new Date(a.timestamp) - new Date(b.timestamp));
      
      const rows = [];
      let lastTimeOut = null;
      let currentSession = null;
      const remarksMap = {};

      // Process records into sessions (In -> Out)
      sortedRecords.forEach((record) => {
        if (record.type === 'time_in') {
          // If there was an open session without Time Out, push it as incomplete
          if (currentSession) {
            rows.push(currentSession);
          }
          
          // Start new session
          currentSession = {
            id: record.id,
            name: user.full_name,
            accountName: record.comment || '',
            timeIn: record.timestamp,
            timeOut: null,
            travelTime: null,
            remarks: record.remarks || ''
          };
          
          // Calculate travel time if there was a previous session
          if (lastTimeOut) {
            const timeInDate = new Date(record.timestamp);
            const prevOutDate = new Date(lastTimeOut);
            const diffMs = timeInDate - prevOutDate;
            
            // Format travel time
            const diffMins = Math.floor(diffMs / 60000);
            const hours = Math.floor(diffMins / 60);
            const mins = diffMins % 60;
            currentSession.travelTime = `${hours > 0 ? `${hours}h ` : ''}${mins}m`;
          } else {
            currentSession.travelTime = 'N/A'; // First session
          }
          
          // Initialize remarks state
          remarksMap[record.id] = record.remarks || '';
          
        } else if (record.type === 'time_out') {
          if (currentSession) {
            currentSession.timeOut = record.timestamp;
            lastTimeOut = record.timestamp;
            rows.push(currentSession);
            currentSession = null;
          } else {
            // Orphaned time_out (shouldn't happen often, but handle it)
            // Maybe just ignore or log?
          }
        }
      });
      
      // Push any remaining open session
      if (currentSession) {
        rows.push(currentSession);
      }
      
      setDailyReportData(rows);
      setReportRemarks(remarksMap);
      
    } catch (error) {
      console.error('Error fetching daily report:', error);
      toast.error('Failed to load daily report');
    } finally {
      setIsLoadingReport(false);
    }
  };

  const handleSaveRemarks = async () => {
    try {
      const updatePromises = Object.entries(reportRemarks).map(async ([id, remark]) => {
        // Only update if remark has changed from original data (optimization)
        const originalRow = dailyReportData.find(row => row.id === id);
        if (originalRow && originalRow.remarks !== remark) {
          return axiosInstance.put(`/attendance/${id}/remarks`, { remarks: remark });
        }
        return Promise.resolve();
      });

      await Promise.all(updatePromises);
      toast.success('Remarks saved successfully');
      fetchDailyReport(); // Refresh data
    } catch (error) {
      console.error('Error saving remarks:', error);
      toast.error('Failed to save remarks');
    }
  };

  const handleExportReport = () => {
    if (dailyReportData.length === 0) {
      toast.error('No data to export');
      return;
    }

    // CSV Header
    const headers = ['Name', 'Account Name', 'Time In', 'Time Out', 'Travel Time', 'Remarks'];
    
    // CSV Rows
    const csvRows = dailyReportData.map(row => [
      `"${row.name}"`,
      `"${row.accountName.replace(/"/g, '""')}"`, // Escape quotes
      formatTaipeiTime(row.timeIn),
      row.timeOut ? formatTaipeiTime(row.timeOut) : '',
      row.travelTime || '',
      `"${(reportRemarks[row.id] || '').replace(/"/g, '""')}"`
    ]);

    const csvContent = [
      headers.join(','),
      ...csvRows.map(row => row.join(','))
    ].join('\n');

    // Download
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', `Daily_Report_${user.full_name}_${dailyReportDate}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  useEffect(() => {
    if (isDailyReportModalOpen) {
      fetchDailyReport();
    }
  }, [isDailyReportModalOpen, dailyReportDate]);

  const canTimeIn = () => {
    const lastTimeIn = getLastTimeIn();
    const lastTimeOut = getLastTimeOut();
    
    // Can time in if:
    // 1. No time in records today, OR
    // 2. Last time out is more recent than last time in
    if (!lastTimeIn) return true; // No time in today
    if (!lastTimeOut) return false; // Timed in but never timed out
    
    return new Date(normalizeTs(lastTimeOut.timestamp)) > new Date(normalizeTs(lastTimeIn.timestamp)); // Last action was time out
  };

  const canTimeOut = () => {
    const lastTimeIn = getLastTimeIn();
    const lastTimeOut = getLastTimeOut();
    
    console.log('canTimeOut check:', {
      totalRecords: todayAttendance.length,
      lastTimeIn: lastTimeIn ? new Date(normalizeTs(lastTimeIn.timestamp)).toLocaleTimeString() : 'none',
      lastTimeOut: lastTimeOut ? new Date(normalizeTs(lastTimeOut.timestamp)).toLocaleTimeString() : 'none'
    });
    
    // Can time out if:
    // 1. There's a time in record today, AND
    // 2. Either no time out exists OR last time in is more recent than last time out
    if (!lastTimeIn) {
      console.log('canTimeOut: false - no time in record');
      return false; // Haven't timed in yet
    }
    if (!lastTimeOut) {
      console.log('canTimeOut: true - timed in but never timed out');
      return true; // Timed in but never timed out
    }
    
    const canTimeout = new Date(normalizeTs(lastTimeIn.timestamp)) > new Date(normalizeTs(lastTimeOut.timestamp));
    console.log('canTimeOut:', canTimeout, '- last action was', canTimeout ? 'time in' : 'time out');
    return canTimeout; // Last action was time in
  };

  const isWorkingHours = () => {
    if (!schedule?.start_time || !schedule?.end_time) return true;

    const now = new Date();
    const currentTime = now.getHours() * 100 + now.getMinutes();
    const startTime = parseInt(schedule.start_time.replace(':', ''));
    const endTime = parseInt(schedule.end_time.replace(':', ''));

    return currentTime >= startTime && currentTime <= endTime;
  };

  const getTodaySchedule = () => {
    if (!schedule?.start_time || !schedule?.end_time) return null;
    return schedule;
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-indigo-50 to-purple-50">
      {/* Header */}
      <header className="bg-white shadow-sm border-b border-gray-100">
        <div className="max-w-4xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <img 
                src="https://customer-assets.emergentagent.com/job_hr-system-repair-1/artifacts/au3caisj_iSource%20LOGO.png"
                alt="iSOURCE Logo"
                className="h-8 w-auto object-contain"
              />
              <div>
                <h1 className="text-xl font-semibold text-gray-900">{user.full_name}</h1>
                <div className="flex items-center space-x-4 text-sm text-gray-500">
                  <span>{user.employee_id || user.email}</span>
                  {user.position && <span>• {sanitizeDisplayText(user.position)}</span>}
                  {user.department && <span>• {sanitizeDisplayText(user.department)}</span>}
                  {user.branch && <span>• {sanitizeDisplayText(user.branch)}</span>}
                </div>
              </div>
            </div>
            <Button
              onClick={onLogout}
              variant="ghost"
              size="sm"
              className="text-gray-600 hover:text-gray-900"
            >
              <LogOut className="h-4 w-4 mr-2" />
              Logout
            </Button>
          </div>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-4 py-8">
        <div className="space-y-8">
          {/* Current Time Display */}
          <Card className="glass shadow-xl border-0 fade-in">
            <CardContent className="text-center py-12">
              <div className="space-y-4">
                <div className="digital-clock">
                  <div className="text-xl text-gray-600 font-medium">
                    {formatDate(currentTime)}
                  </div>
                  <div className="text-6xl md:text-7xl font-bold text-gray-900 mt-2">
                    {formatTime(currentTime)}
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Schedule Display */}
          {schedule && (
            <Card className="glass shadow-lg border-0 fade-in" style={{ animationDelay: '0.1s' }}>
              <CardContent>
                {getTodaySchedule() ? (
                  <div className="space-y-4 py-2">
                    <div className="text-center">
                      <div className="text-xl text-gray-600 font-medium">
                        Work Hour
                      </div>
                      <div className="text-3xl md:text-4xl font-bold text-gray-900 mt-2">
                        {formatHM12(getTodaySchedule().start_time)} - {formatHM12(getTodaySchedule().end_time)}
                      </div>
                    </div>
                  </div>
                ) : (
                  <div className="text-center py-4">
                    <div className="text-sm text-gray-500">No assigned schedule</div>
                  </div>
                )}
              </CardContent>
            </Card>
          )}

          {/* Comment Box for Field Employees */}
          <Card className="glass shadow-lg border-0 fade-in" style={{ animationDelay: '0.2s' }}>
            <CardHeader>
              <CardTitle className="text-lg font-semibold text-gray-900 flex items-center">
                <MessageSquare className="h-5 w-5 text-indigo-600 mr-2" />
                Field Notes / Comments
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                <Label htmlFor="comment" className="text-sm font-medium text-gray-700">
                  Add a note with your attendance (optional)
                </Label>
                <Textarea
                  id="comment"
                  value={comment}
                  onChange={(e) => setComment(e.target.value)}
                  className="min-h-[80px] resize-none"
                  maxLength={500}
                />
                <div className="flex justify-between text-xs text-gray-500">
                  <span>This note will be attached to your next time in/out record</span>
                  <span>{comment.length}/500</span>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Attendance Buttons */}
          <div className="grid md:grid-cols-2 gap-6">
            <Card className="glass shadow-lg border-0 card-hover fade-in" style={{ animationDelay: '0.3s' }}>
              <CardHeader className="text-center">
                <CardTitle className="text-lg font-semibold text-gray-900 flex items-center justify-center">
                  <CheckCircle className="h-5 w-5 text-green-600 mr-2" />
                  Time In
                </CardTitle>
              </CardHeader>
              <CardContent className="text-center pb-6">
                <Button
                  onClick={() => handleAttendance('time_in')}
                  disabled={!canTimeIn() || isSubmitting}
                  className="w-full py-6 text-lg font-medium bg-gradient-to-r from-green-600 to-emerald-600 hover:from-green-700 hover:to-emerald-700 btn-animate"
                >
                  {isSubmitting && isLoadingLocation ? (
                    <div className="flex items-center justify-center">
                      <div className="loading-spinner mr-2"></div>
                      Getting Location...
                    </div>
                  ) : (
                    <>
                      <Camera className="h-5 w-5 mr-2" />
                      {canTimeIn() ? 'Clock In with Photo' : 'Already Clocked In'}
                    </>
                  )}
                </Button>
                {getLastTimeIn() && (
                  <p className="text-sm text-gray-500 mt-3">
                    Last Time In: {formatTimestampFromBackend(getLastTimeIn().timestamp)}
                  </p>
                )}
              </CardContent>
            </Card>

            <Card className="glass shadow-lg border-0 card-hover fade-in" style={{ animationDelay: '0.4s' }}>
              <CardHeader className="text-center">
                <CardTitle className="text-lg font-semibold text-gray-900 flex items-center justify-center">
                  <XCircle className="h-5 w-5 text-red-600 mr-2" />
                  Time Out
                </CardTitle>
              </CardHeader>
              <CardContent className="text-center pb-6">
                <Button
                  onClick={() => handleAttendance('time_out')}
                  disabled={!canTimeOut() || isSubmitting}
                  className="w-full py-6 text-lg font-medium bg-gradient-to-r from-red-600 to-rose-600 hover:from-red-700 hover:to-rose-700 btn-animate"
                >
                  {isSubmitting && isLoadingLocation ? (
                    <div className="flex items-center justify-center">
                      <div className="loading-spinner mr-2"></div>
                      Getting Location...
                    </div>
                  ) : (
                    <>
                      <Camera className="h-5 w-5 mr-2" />
                      {canTimeOut() ? 'Clock Out with Photo' : (getLastTimeIn() ? 'Already Clocked Out' : 'Clock In First')}
                    </>
                  )}
                </Button>
                {getLastTimeOut() && (
                  <p className="text-sm text-gray-500 mt-3">
                    Last Time Out: {formatTimestampFromBackend(getLastTimeOut().timestamp)}
                  </p>
                )}
              </CardContent>
            </Card>
          </div>

          {/* Location Status */}
          {location && (
            <Card className="glass shadow-lg border-0 fade-in" style={{ animationDelay: '0.5s' }}>
              <CardHeader>
                <CardTitle className="text-lg font-semibold text-gray-900 flex items-center">
                  <MapPin className="h-5 w-5 text-indigo-600 mr-2" />
                  Current Location
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-2 text-sm text-gray-600">
                  <p><strong>Latitude:</strong> {location.latitude.toFixed(6)}</p>
                  <p><strong>Longitude:</strong> {location.longitude.toFixed(6)}</p>
                  <p><strong>Accuracy:</strong> Â±{location.accuracy?.toFixed(0) || 'N/A'} meters</p>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Today's Activity */}
          {todayAttendance.length > 0 && (
            <Card className="glass shadow-lg border-0 fade-in" style={{ animationDelay: '0.6s' }}>
              <CardHeader>
                <CardTitle className="text-lg font-semibold text-gray-900 flex items-center">
                  <Calendar className="h-5 w-5 text-indigo-600 mr-2" />
                  Today's Activity
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  {todayAttendance
                    .sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp))
                    .map((record, index) => (
                    <div
                      key={record.id}
                      className="flex items-start justify-between p-3 bg-white bg-opacity-50 rounded-lg"
                    >
                      <div className="flex items-start space-x-3">
                        {record.type === 'time_in' ? (
                          <CheckCircle className="h-5 w-5 text-green-600 mt-0.5" />
                        ) : (
                          <XCircle className="h-5 w-5 text-red-600 mt-0.5" />
                        )}
                        <div className="flex-1">
                          <p className="font-medium text-gray-900 capitalize">
                            {record.type.replace('_', ' ')}
                          </p>
                          <p className="text-sm text-gray-500">
                            {formatTimestampFromBackend(record.timestamp)}
                          </p>
                          {record.comment && (
                            <div className="mt-2 p-2 bg-blue-50 rounded-md">
                              <p className="text-sm text-blue-800">
                                <MessageSquare className="h-3 w-3 inline mr-1" />
                                {record.comment}
                              </p>
                            </div>
                          )}
                        </div>
                      </div>
                      {record.location && (
                        <MapPin className="h-4 w-4 text-gray-400 mt-0.5" />
                      )}
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}

          {/* View Logs Button */}
          <div className="mt-8 flex justify-center gap-4 fade-in flex-wrap" style={{ animationDelay: '0.7s' }}>
            <Button
              onClick={handleViewLogs}
              className="bg-indigo-600 hover:bg-indigo-700 text-white px-8 py-6 text-lg shadow-lg w-56 justify-center"
            >
              <FileText className="h-5 w-5 mr-2" />
              View Logs
            </Button>

            <Button
              onClick={() => setIsAnnouncementModalOpen(true)}
              className="bg-amber-600 hover:bg-amber-700 text-white px-8 py-6 text-lg shadow-lg w-56 justify-center"
            >
              <Megaphone className="h-5 w-5 mr-2" />
              Announcements
            </Button>

            <Button
              onClick={() => setIsDailyReportModalOpen(true)}
              className="bg-teal-600 hover:bg-teal-700 text-white px-8 py-6 text-lg shadow-lg w-56 justify-center"
            >
              <FileText className="h-5 w-5 mr-2" />
              Daily Report
            </Button>

            {isItineraryEnrolled && (
              <Button
                onClick={handleOpenItineraryModal}
                className="bg-green-600 hover:bg-green-700 text-white px-8 py-6 text-lg shadow-lg w-56 justify-center"
              >
                <Calendar className="h-5 w-5 mr-2" />
                Itinerary
              </Button>
            )}

            {payslipEnabled && (
              <Button
                onClick={handleViewPayslips}
                className="bg-emerald-600 hover:bg-emerald-700 text-white px-8 py-6 text-lg shadow-lg w-56 justify-center"
              >
                <FileText className="h-5 w-5 mr-2" />
                Payslip
              </Button>
            )}


            {user.branch === 'Technical' && (
              <Button
                onClick={handleOpenFSRList}
                className="bg-blue-600 hover:bg-blue-700 text-white px-8 py-6 text-lg shadow-lg w-56 justify-center relative overflow-visible"
              >
                <ClipboardList className="h-5 w-5 mr-2" />
                Field Service Report
                {pendingFSRCount > 0 && (
                  <span className="absolute -top-2 -right-2 bg-red-600 text-white text-xs font-bold px-2 py-1 rounded-full shadow-md border-2 border-white z-50">
                    {pendingFSRCount}
                  </span>
                )}
              </Button>
            )}
          </div>
        </div>
      </main>

      {/* Daily Report Modal */}
      <Dialog open={isDailyReportModalOpen} onOpenChange={setIsDailyReportModalOpen}>
        <DialogContent className="max-w-6xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-2xl font-bold text-gray-900">Daily Report</DialogTitle>
            <DialogDescription>
              View and manage your daily activity report
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            {/* Date Filter */}
            <div className="flex items-center gap-4 bg-gray-50 p-4 rounded-lg">
              <div className="flex-1 max-w-xs">
                <Label htmlFor="reportDate" className="text-sm font-medium text-gray-700 mb-1 block">
                  Report Date
                </Label>
                <Input
                  id="reportDate"
                  type="date"
                  value={dailyReportDate}
                  onChange={(e) => setDailyReportDate(e.target.value)}
                  className="bg-white"
                />
              </div>
            </div>

            {/* Report Table */}
            <div className="border rounded-lg overflow-hidden">
              <Table>
                <TableHeader>
                  <TableRow className="bg-gray-50">
                    <TableHead className="font-semibold text-gray-900">Name</TableHead>
                    <TableHead className="font-semibold text-gray-900">Account Name</TableHead>
                    <TableHead className="font-semibold text-gray-900">Time In</TableHead>
                    <TableHead className="font-semibold text-gray-900">Time Out</TableHead>
                    <TableHead className="font-semibold text-gray-900">Travel Time</TableHead>
                    <TableHead className="font-semibold text-gray-900 min-w-[200px]">Remarks</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {isLoadingReport ? (
                    <TableRow>
                      <TableCell colSpan={6} className="text-center py-8 text-gray-500">
                        Loading report data...
                      </TableCell>
                    </TableRow>
                  ) : dailyReportData.length > 0 ? (
                    dailyReportData.map((row, index) => (
                      <TableRow key={index}>
                        <TableCell className="font-medium">{row.name}</TableCell>
                        <TableCell>{row.accountName}</TableCell>
                        <TableCell>{formatTaipeiTime(row.timeIn)}</TableCell>
                        <TableCell>{row.timeOut ? formatTaipeiTime(row.timeOut) : '-'}</TableCell>
                        <TableCell>{row.travelTime}</TableCell>
                        <TableCell>
                          <Input
                            value={reportRemarks[row.id] || ''}
                            onChange={(e) => setReportRemarks({
                              ...reportRemarks,
                              [row.id]: e.target.value
                            })}
                            placeholder="Enter remarks..."
                            className="h-8 text-sm"
                          />
                        </TableCell>
                      </TableRow>
                    ))
                  ) : (
                    <TableRow>
                      <TableCell colSpan={6} className="text-center py-8 text-gray-500">
                        No records found for this date
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </div>
          </div>

            {/* Actions */}
            <div className="flex justify-end gap-3 mt-4 pt-4 border-t">
              <Button
                variant="outline"
                onClick={handleExportReport}
                className="flex items-center gap-2"
                disabled={dailyReportData.length === 0}
              >
                <Download className="h-4 w-4" />
                Export CSV
              </Button>
              <Button
                onClick={handleSaveRemarks}
                className="bg-indigo-600 hover:bg-indigo-700 flex items-center gap-2"
                disabled={dailyReportData.length === 0}
              >
                <Save className="h-4 w-4" />
                Save Changes
              </Button>
            </div>
        </DialogContent>
      </Dialog>

      <Dialog open={isItineraryModalOpen} onOpenChange={setIsItineraryModalOpen}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-2xl font-bold text-gray-900">Today's Itineraries</DialogTitle>
          </DialogHeader>

          {isLoadingItineraryToday ? (
            <div className="py-10 text-center text-gray-500">Loading itineraries...</div>
          ) : (Array.isArray(itineraryTodayItems) && itineraryTodayItems.length > 0) ? (
            <div className="space-y-4">
              <Tabs value={String(activeItineraryRecordId || '')} onValueChange={setActiveItineraryRecordId}>
                <TabsList className="w-full justify-start overflow-x-auto">
                  {itineraryTodayItems.map((item) => (
                    <TabsTrigger key={item.id} value={String(item.id)} className="shrink-0">
                      {String(item?.account_name || '')}
                    </TabsTrigger>
                  ))}
                </TabsList>
              </Tabs>

              {activeItineraryItem ? (
                <div className="space-y-3">
                  <div className="border rounded-lg bg-white p-4">
                    <div className="text-sm font-semibold text-gray-900">Address</div>
                    <div className="mt-2 text-sm text-gray-700 whitespace-pre-wrap break-words">
                      {activeItineraryItem?.address ? String(activeItineraryItem.address) : '-'}
                    </div>
                  </div>

                  <div className="border rounded-lg bg-white p-4">
                    <div className="text-sm font-semibold text-gray-900">Location</div>
                    <div className="mt-2 text-sm text-gray-700 whitespace-pre-wrap break-words">
                      {activeItineraryItem?.location ? String(activeItineraryItem.location) : '-'}
                    </div>
                  </div>

                  <button
                    type="button"
                    onClick={() => handleOpenTaskModal('priority')}
                    className="w-full border rounded-lg bg-white p-4 text-left hover:bg-gray-50 transition-colors"
                  >
                    <div className="text-sm font-semibold text-gray-900">Priority Task</div>
                    <div className="mt-2 space-y-1">
                      {Array.isArray(activeItineraryItem?.priorities) && activeItineraryItem.priorities.length > 0 ? (
                        activeItineraryItem.priorities.map((task, idx) => (
                          <div key={`${task}-${idx}`} className="text-sm text-gray-700 break-words">
                            {task}
                          </div>
                        ))
                      ) : (
                        <div className="text-sm text-gray-500">No priority tasks</div>
                      )}
                    </div>
                  </button>

                  <button
                    type="button"
                    onClick={() => handleOpenTaskModal('todo')}
                    className="w-full border rounded-lg bg-white p-4 text-left hover:bg-gray-50 transition-colors"
                  >
                    <div className="text-sm font-semibold text-gray-900">To-Do List</div>
                    <div className="mt-2 space-y-1">
                      {Array.isArray(activeItineraryItem?.todo_items) && activeItineraryItem.todo_items.length > 0 ? (
                        activeItineraryItem.todo_items.map((task, idx) => (
                          <div key={`${task}-${idx}`} className="text-sm text-gray-700 break-words">
                            {task}
                          </div>
                        ))
                      ) : (
                        <div className="text-sm text-gray-500">No to-do items</div>
                      )}
                    </div>
                  </button>

                  <div className="border rounded-lg bg-white p-4">
                    <div className="text-sm font-semibold text-gray-900">Status</div>
                    <div className="mt-4 flex flex-col items-center justify-center gap-3">
                      <div
                        className={
                          activeItineraryItem?.status_cancelled
                            ? 'text-red-600 font-bold text-lg'
                            : activeItineraryItem?.status_finished
                              ? 'text-green-600 font-bold text-lg'
                              : 'text-gray-400 font-bold text-lg'
                        }
                      >
                        {activeItineraryItem?.status_cancelled
                          ? 'Cancelled'
                          : activeItineraryItem?.status_finished
                            ? 'Finished'
                            : 'On Queue'}
                      </div>
                      <div className="flex items-end justify-center gap-7">
                        <div className="flex flex-col items-center gap-1">
                          <div className="text-[11px] font-semibold text-gray-600">Abort</div>
                          <button
                            type="button"
                            onClick={handleOpenCancellationModal}
                            disabled={isSavingItineraryStatus}
                            className={
                              activeItineraryItem?.status_cancelled
                                ? 'h-10 w-10 rounded-full bg-red-600 border-2 border-red-600 flex items-center justify-center'
                                : 'h-10 w-10 rounded-full border-2 border-gray-400 flex items-center justify-center'
                            }
                          >
                            {activeItineraryItem?.status_cancelled ? <XCircle className="h-6 w-6 text-white" /> : null}
                          </button>
                        </div>

                        <div className="flex flex-col items-center gap-1">
                          <div className="text-[11px] font-semibold text-gray-600">Finished</div>
                          <button
                            type="button"
                            onClick={handleRequestFinishItinerary}
                            disabled={isSavingItineraryStatus}
                            className={
                              activeItineraryItem?.status_finished
                                ? 'h-10 w-10 rounded-full bg-green-600 border-2 border-green-600 flex items-center justify-center'
                                : 'h-10 w-10 rounded-full border-2 border-gray-400 flex items-center justify-center'
                            }
                          >
                            {activeItineraryItem?.status_finished ? <Check className="h-6 w-6 text-white" /> : null}
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              ) : (
                <div className="py-10 text-center text-gray-500">Select an account</div>
              )}
            </div>
          ) : (
            <div className="py-10 text-center text-gray-500">No itineraries for today</div>
          )}
        </DialogContent>
      </Dialog>

      <Dialog
        open={isCancellationModalOpen}
        onOpenChange={(open) => {
          setIsCancellationModalOpen(open);
          if (!open) {
            setCancellationError('');
          }
        }}
      >
        <DialogContent className="max-w-xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-2xl font-bold text-gray-900">Reason of Cancellation</DialogTitle>
          </DialogHeader>

          <div className="space-y-3">
            <Textarea
              value={cancellationDraft}
              onChange={(event) => setCancellationDraft(event.target.value)}
              placeholder="Type reason..."
              className="min-h-[80px] resize-none"
              onInput={(event) => {
                const target = event.target;
                target.style.height = 'auto';
                target.style.height = `${target.scrollHeight}px`;
              }}
            />
            {cancellationError ? (
              <div className="text-sm font-medium text-red-600">{cancellationError}</div>
            ) : null}
          </div>

          <div className="flex justify-end gap-3 mt-4 pt-4 border-t">
            <Button
              onClick={handleSaveCancellationReason}
              className="bg-green-600 hover:bg-green-700"
            >
              Save
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog
        open={isOutcomeModalOpen}
        onOpenChange={(open) => {
          setIsOutcomeModalOpen(open);
          if (!open) {
            setOutcomeError('');
            setIsOutcomeStartTimePickerOpen(false);
            setIsOutcomeEndTimePickerOpen(false);
          }
        }}
      >
        <DialogContent className="max-w-xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-2xl font-bold text-gray-900">Itinerary Outcome</DialogTitle>
            <DialogDescription>Fill up all fields before saving.</DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
              <div className="space-y-2">
                <Label className="text-sm font-semibold text-gray-900">Start Time</Label>
                <div className="flex items-center gap-2">
                  <Input
                    readOnly
                    value={buildOutcomeTimeValue({
                      hour: outcomeForm.startTimeHour,
                      minute: outcomeForm.startTimeMinute,
                      period: outcomeForm.startTimePeriod,
                    })}
                    placeholder="HH:MM AM/PM"
                  />
                  <Popover open={isOutcomeStartTimePickerOpen} onOpenChange={setIsOutcomeStartTimePickerOpen}>
                    <PopoverTrigger asChild>
                      <Button type="button" variant="outline" size="icon" className="shrink-0">
                        <Clock className="h-4 w-4" />
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent align="end" className="w-[320px] p-3">
                      <div className="grid grid-cols-3 gap-2">
                        <Select
                          value={String(outcomeForm.startTimeHour || '')}
                          onValueChange={(value) => updateOutcomeFormField('startTimeHour', value)}
                        >
                          <SelectTrigger>
                            <SelectValue placeholder="Hour" />
                          </SelectTrigger>
                          <SelectContent>
                            {Array.from({ length: 12 }, (_, idx) => `${idx + 1}`).map((value) => (
                              <SelectItem key={`outcome-start-hour-${value}`} value={value}>
                                {value}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>

                        <Select
                          value={String(outcomeForm.startTimeMinute || '00')}
                          onValueChange={(value) => updateOutcomeFormField('startTimeMinute', value)}
                        >
                          <SelectTrigger>
                            <SelectValue placeholder="Minute" />
                          </SelectTrigger>
                          <SelectContent>
                            {Array.from({ length: 60 }, (_, idx) => `${idx}`.padStart(2, '0')).map((value) => (
                              <SelectItem key={`outcome-start-minute-${value}`} value={value}>
                                {value}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>

                        <Select
                          value={String(outcomeForm.startTimePeriod || 'AM')}
                          onValueChange={(value) => updateOutcomeFormField('startTimePeriod', value)}
                        >
                          <SelectTrigger>
                            <SelectValue placeholder="AM/PM" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="AM">AM</SelectItem>
                            <SelectItem value="PM">PM</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                    </PopoverContent>
                  </Popover>
                </div>
              </div>

              <div className="space-y-2">
                <Label className="text-sm font-semibold text-gray-900">End Time</Label>
                <div className="flex items-center gap-2">
                  <Input
                    readOnly
                    value={buildOutcomeTimeValue({
                      hour: outcomeForm.endTimeHour,
                      minute: outcomeForm.endTimeMinute,
                      period: outcomeForm.endTimePeriod,
                    })}
                    placeholder="HH:MM AM/PM"
                  />
                  <Popover open={isOutcomeEndTimePickerOpen} onOpenChange={setIsOutcomeEndTimePickerOpen}>
                    <PopoverTrigger asChild>
                      <Button type="button" variant="outline" size="icon" className="shrink-0">
                        <Clock className="h-4 w-4" />
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent align="end" className="w-[320px] p-3">
                      <div className="grid grid-cols-3 gap-2">
                        <Select
                          value={String(outcomeForm.endTimeHour || '')}
                          onValueChange={(value) => updateOutcomeFormField('endTimeHour', value)}
                        >
                          <SelectTrigger>
                            <SelectValue placeholder="Hour" />
                          </SelectTrigger>
                          <SelectContent>
                            {Array.from({ length: 12 }, (_, idx) => `${idx + 1}`).map((value) => (
                              <SelectItem key={`outcome-end-hour-${value}`} value={value}>
                                {value}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>

                        <Select
                          value={String(outcomeForm.endTimeMinute || '00')}
                          onValueChange={(value) => updateOutcomeFormField('endTimeMinute', value)}
                        >
                          <SelectTrigger>
                            <SelectValue placeholder="Minute" />
                          </SelectTrigger>
                          <SelectContent>
                            {Array.from({ length: 60 }, (_, idx) => `${idx}`.padStart(2, '0')).map((value) => (
                              <SelectItem key={`outcome-end-minute-${value}`} value={value}>
                                {value}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>

                        <Select
                          value={String(outcomeForm.endTimePeriod || 'AM')}
                          onValueChange={(value) => updateOutcomeFormField('endTimePeriod', value)}
                        >
                          <SelectTrigger>
                            <SelectValue placeholder="AM/PM" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="AM">AM</SelectItem>
                            <SelectItem value="PM">PM</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                    </PopoverContent>
                  </Popover>
                </div>
              </div>
            </div>

            <div className="space-y-2">
              <Label className="text-sm font-semibold text-gray-900">Contact Person</Label>
              <Input
                value={outcomeForm.contactPerson}
                onChange={(event) => updateOutcomeFormField('contactPerson', event.target.value)}
                placeholder="Enter contact person"
              />
            </div>

            <div className="space-y-2">
              <Label className="text-sm font-semibold text-gray-900">Position</Label>
              <Input
                value={outcomeForm.position}
                onChange={(event) => updateOutcomeFormField('position', event.target.value)}
                placeholder="Enter position"
              />
            </div>

            <div className="space-y-2">
              <Label className="text-sm font-semibold text-gray-900">Contact #</Label>
              <Input
                inputMode="numeric"
                value={outcomeForm.contactNumber}
                onChange={(event) => updateOutcomeFormField('contactNumber', event.target.value)}
                placeholder="Enter contact number"
              />
            </div>

            <div className="space-y-2">
              <Label className="text-sm font-semibold text-gray-900">Result</Label>
              <Textarea
                value={outcomeForm.result}
                onChange={(event) => updateOutcomeFormField('result', event.target.value)}
                placeholder="Type itinerary outcome..."
                className="min-h-[80px] resize-none"
                onInput={(event) => {
                  const target = event.target;
                  target.style.height = 'auto';
                  target.style.height = `${target.scrollHeight}px`;
                }}
              />
            </div>

            {outcomeError ? (
              <div className="text-sm font-medium text-red-600">{outcomeError}</div>
            ) : null}
          </div>

          <div className="flex justify-end gap-3 mt-4 pt-4 border-t">
            <Button
              onClick={handleSaveItineraryOutcome}
              className="bg-green-600 hover:bg-green-700"
            >
              Save
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      <AlertDialog
        open={isOutcomeValidationDialogOpen}
        onOpenChange={setIsOutcomeValidationDialogOpen}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Incomplete Fields</AlertDialogTitle>
            <AlertDialogDescription>
              All fields should be filled up to save.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogAction
              type="button"
              onClick={() => setIsOutcomeValidationDialogOpen(false)}
              className="bg-green-600 hover:bg-green-700"
            >
              OK
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog
        open={isStatusConfirmOpen}
        onOpenChange={(open) => {
          setIsStatusConfirmOpen(open);
          if (!open) {
            setPendingStatusPayload(null);
            setStatusConfirmType(null);
          }
        }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              {statusConfirmType === 'cancel'
                ? 'Confirm Cancellation'
                : statusConfirmType === 'finish'
                  ? 'Confirm Finish'
                  : 'Confirm'}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {statusConfirmType === 'cancel'
                ? `Are you sure you want to cancel ${pendingItineraryRecord?.account_name || ''}?`
                : statusConfirmType === 'finish'
                  ? `Are you sure you want to mark ${pendingItineraryRecord?.account_name || ''} as ${pendingStatusPayload?.finished ? 'Finished' : 'On Queue'}?`
                  : ''}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={isSavingItineraryStatus}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              disabled={isSavingItineraryStatus}
              onClick={handleConfirmStatusUpdate}
              className={statusConfirmType === 'cancel' ? 'bg-red-600 hover:bg-red-700' : 'bg-green-600 hover:bg-green-700'}
            >
              Confirm
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <Dialog
        open={isTaskModalOpen}
        onOpenChange={(open) => {
          setIsTaskModalOpen(open);
          if (!open) {
            setTaskModalType(null);
            setTaskDraftItems([]);
            setIsSavingTasks(false);
          }
        }}
      >
        <DialogContent className="max-w-xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-2xl font-bold text-gray-900">
              {taskModalType === 'priority' ? 'Priority Task' : 'To-Do List'}
            </DialogTitle>
          </DialogHeader>

          {taskDraftItems.length === 0 ? (
            <div className="py-10 text-center text-gray-500">No tasks</div>
          ) : (
            <div className="space-y-2">
              {taskDraftItems.map((task, idx) => (
                <div key={`${task.position ?? idx}-${task.text}`} className="flex items-center justify-between gap-4 p-3 rounded-lg border bg-white">
                  <div className="text-sm text-gray-900 break-words flex-1">{task.text}</div>
                  <button type="button" onClick={() => toggleTaskDraftItem(idx)} className="flex items-center gap-3 shrink-0">
                    <div className={task.finished
                      ? 'h-8 w-8 rounded-full bg-green-600 border-2 border-green-600 flex items-center justify-center'
                      : 'h-8 w-8 rounded-full border-2 border-gray-400 flex items-center justify-center'}
                    >
                      {task.finished ? <Check className="h-5 w-5 text-white" /> : null}
                    </div>
                    <div className="text-sm font-medium text-gray-700">Done</div>
                  </button>
                </div>
              ))}
            </div>
          )}

          <div className="flex justify-end gap-3 mt-4 pt-4 border-t">
            <Button
              onClick={handleSaveTaskUpdates}
              disabled={isSavingTasks || !taskModalType || !activeItineraryItem?.id}
              className="bg-green-600 hover:bg-green-700"
            >
              {isSavingTasks ? 'Updating...' : 'Update Task'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Camera Capture Modal */}
      <CameraCapture 
        isOpen={isCameraOpen}
        onClose={() => {
          setIsCameraOpen(false);
          setPendingAttendanceType(null);
        }}
        onCapture={handlePhotoCapture}
        attendanceType={pendingAttendanceType}
        previewZoom={1.5}
        requireRearCapture={true}
      />

      {/* Attendance History Modal */}
      <Dialog open={isLogsModalOpen} onOpenChange={setIsLogsModalOpen}>
        <DialogContent className="max-w-5xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-2xl font-bold text-gray-900">Attendance History</DialogTitle>
            <DialogDescription>
              View all your attendance records with optional date filtering
            </DialogDescription>
          </DialogHeader>

          {/* Date Filter Section */}
          <div className="bg-gray-50 p-4 rounded-lg space-y-4">
            <div className="flex items-center gap-2 mb-3">
              <Filter className="h-5 w-5 text-indigo-600" />
              <h3 className="font-semibold text-gray-900">Date Filter</h3>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label htmlFor="startDate" className="text-sm font-medium text-gray-700">
                  Start Date
                </Label>
                <Input
                  id="startDate"
                  type="date"
                  value={logStartDate}
                  onChange={(e) => setLogStartDate(e.target.value)}
                  className="mt-1"
                />
              </div>
              <div>
                <Label htmlFor="endDate" className="text-sm font-medium text-gray-700">
                  End Date
                </Label>
                <Input
                  id="endDate"
                  type="date"
                  value={logEndDate}
                  onChange={(e) => setLogEndDate(e.target.value)}
                  className="mt-1"
                />
              </div>
            </div>

            <div className="flex gap-2">
              <Button
                onClick={handleApplyDateFilter}
                className="bg-indigo-600 hover:bg-indigo-700"
                disabled={isLoadingHistory}
              >
                Apply Filter
              </Button>
              <Button
                onClick={handleClearDateFilter}
                variant="outline"
                disabled={isLoadingHistory}
              >
                Clear Filter
              </Button>
            </div>
          </div>

          {/* Attendance Records Table */}
          <div className="overflow-x-auto rounded-lg border border-gray-200">
            <table className="w-full text-sm text-left">
              <thead className="bg-gray-50 text-gray-700 font-medium">
                <tr>
                  <th className="px-6 py-3">Date</th>
                  <th className="px-6 py-3">Time In</th>
                  <th className="px-6 py-3">Time Out</th>
                  <th className="px-6 py-3">Status</th>
                  <th className="px-6 py-3">Work Hours</th>
                  <th className="px-6 py-3">Location</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200 bg-white">
                {attendanceHistory.length > 0 ? (
                  attendanceHistory.map((record) => (
                    <tr key={record.id} className="hover:bg-gray-50 transition-colors">
                      <td className="px-6 py-4 font-medium text-gray-900">
                        {fmtTaipeiDate(record.date)}
                      </td>
                      <td className="px-6 py-4 text-green-600">
                        {record.time_in ? fmtTaipeiTime(record.time_in) : '-'}
                      </td>
                      <td className="px-6 py-4 text-red-600">
                        {record.time_out ? fmtTaipeiTime(record.time_out) : '-'}
                      </td>
                      <td className="px-6 py-4">
                        <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                          record.status === 'Present' ? 'bg-green-100 text-green-800' :
                          record.status === 'Late' ? 'bg-yellow-100 text-yellow-800' :
                          'bg-gray-100 text-gray-800'
                        }`}>
                          {record.status}
                        </span>
                      </td>
                      <td className="px-6 py-4 text-gray-600">
                        {record.work_hours ? `${record.work_hours} hrs` : '-'}
                      </td>
                      <td className="px-6 py-4 text-gray-500 max-w-[200px] truncate" title={record.location || 'Unknown'}>
                        {record.location || 'Unknown'}
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan="6" className="px-6 py-8 text-center text-gray-500">
                      {isLoadingHistory ? 'Loading records...' : 'No attendance records found'}
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </DialogContent>
      </Dialog>

      {/* Field Service Report List Modal */}
      <Dialog open={isFSRListModalOpen} onOpenChange={setIsFSRListModalOpen}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-2xl font-bold text-gray-900 flex justify-between items-center">
              <span>Field Service Reports</span>
              <Button onClick={handleCreateNewFSR} className="bg-indigo-600 hover:bg-indigo-700 text-white">
                <Plus className="h-4 w-4 mr-2" />
                New FSR
              </Button>
            </DialogTitle>
            <DialogDescription>
              Manage and view your field service reports
            </DialogDescription>
          </DialogHeader>

          <div className="mt-4">
            {isLoadingFSR ? (
              <div className="text-center py-8 text-gray-500">Loading reports...</div>
            ) : fsrList.length > 0 ? (
              <div className="overflow-x-auto rounded-lg border border-gray-200">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Service Report #</TableHead>
                      <TableHead>Customer Name</TableHead>
                      <TableHead>Date</TableHead>
                      <TableHead className="text-right">Action</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {fsrList.map((report) => (
                      <TableRow key={report.id}>
                        <TableCell className="font-medium">{report.fsr_number}</TableCell>
                        <TableCell>{report.account_name}</TableCell>
                        <TableCell>{fmtTaipeiDate(report.date)}</TableCell>
                        <TableCell className="text-right flex justify-end gap-2">
                          <Button 
                            variant="ghost" 
                            size="icon" 
                            onClick={() => handleMarkAsCompleted(report)}
                            title="Mark as Completed"
                            className="text-green-600 hover:text-green-700 hover:bg-green-50"
                          >
                            <CheckCircle className="h-4 w-4" />
                          </Button>
                          <Button 
                            variant="ghost" 
                            size="icon" 
                            onClick={() => handleEditFSR(report)}
                            title="Update Report"
                            className="text-indigo-600 hover:text-indigo-700 hover:bg-indigo-50"
                          >
                            <Edit className="h-4 w-4" />
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            ) : (
              <div className="text-center py-8 text-gray-500 bg-gray-50 rounded-lg">
                No reports found. Create a new one to get started.
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* Confirmation Dialog for Completion */}
      <Dialog open={isConfirmCompleteOpen} onOpenChange={setIsConfirmCompleteOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="text-xl font-bold text-gray-900">Confirm Completion</DialogTitle>
            <DialogDescription className="pt-2">
              Are you sure you want to mark this report as Completed? This action cannot be undone and will remove the report from your pending list.
            </DialogDescription>
          </DialogHeader>
          <div className="flex justify-end gap-4 mt-4">
            <Button
              variant="outline"
              onClick={() => {
                setIsConfirmCompleteOpen(false);
                setReportToComplete(null);
              }}
            >
              Cancel
            </Button>
            <Button
              className="bg-green-600 hover:bg-green-700 text-white"
              onClick={handleConfirmCompletion}
            >
              Mark as Completed
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Field Service Report Modal */}
      <FieldServiceReportModal 
        isOpen={isFSRModalOpen}
        onClose={handleFSRModalClose}
        user={user}
        axiosInstance={axiosInstance}
        initialData={selectedFSR}
      />

      {/* Compliance Warning Modal */}
      <Dialog open={isComplianceModalOpen} onOpenChange={setIsComplianceModalOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="text-xl font-bold text-red-600 flex items-center">
              <XCircle className="h-6 w-6 mr-2" />
              Requirements Not Met
            </DialogTitle>
            <DialogDescription className="text-gray-700 pt-2 text-base">
              {complianceError || "Photo and location are required to clock in/out."}
            </DialogDescription>
          </DialogHeader>
          <div className="flex justify-end mt-4">
            <Button 
              onClick={() => setIsComplianceModalOpen(false)}
              className="bg-red-600 hover:bg-red-700 text-white"
            >
              Close
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Photo Warning Modal */}
      <Dialog open={isPhotoWarningOpen} onOpenChange={setIsPhotoWarningOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="text-xl font-bold text-amber-600 flex items-center">
              <Camera className="h-6 w-6 mr-2" />
              Photo Recommended
            </DialogTitle>
            <DialogDescription className="text-gray-700 pt-2 text-base">
              A photo is requested to verify your attendance. Are you sure you want to proceed without one?
            </DialogDescription>
          </DialogHeader>
          <div className="flex justify-end gap-3 mt-4">
            <Button 
              onClick={handleAddPhoto}
              className="bg-indigo-600 hover:bg-indigo-700 text-white"
            >
              Add Photo
            </Button>
            <Button 
              onClick={handleProceedWithoutPhoto}
              variant="outline"
              className="border-amber-600 text-amber-700 hover:bg-amber-50"
            >
              Proceed Without Photo
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={isActionNotRecordedOpen} onOpenChange={setIsActionNotRecordedOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="text-center text-xl font-bold text-red-600">
              Action Not Recorded
            </DialogTitle>
            <DialogDescription className="pt-2 text-center text-base text-gray-700">
              Your action could not be recorded due to an unstable or unavailable internet connection. Please ensure you have a secure internet connection and try again.
            </DialogDescription>
          </DialogHeader>
          <div className="mt-4 flex justify-center">
            <Button
              onClick={() => setIsActionNotRecordedOpen(false)}
              className="min-w-28 bg-red-600 text-white hover:bg-red-700"
            >
              OK
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Payslip Modal */}
      <Dialog open={isPayslipModalOpen} onOpenChange={setIsPayslipModalOpen}>
        <DialogContent className="max-w-5xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-2xl font-bold text-gray-900 flex items-center">
              <FileText className="h-6 w-6 text-emerald-600 mr-2" />
              Payslips
            </DialogTitle>
            <DialogDescription>
              View and download your payslips
            </DialogDescription>
          </DialogHeader>

          <Tabs value={payslipTab} onValueChange={setPayslipTab} className="mt-2">
            <TabsList className="grid w-full grid-cols-2">
              <TabsTrigger value="payroll">Payroll</TabsTrigger>
              <TabsTrigger value="mf">MF</TabsTrigger>
            </TabsList>
            <TabsContent value="payroll" className="mt-4">
              {renderPayslipAccordion('payroll')}
            </TabsContent>
            <TabsContent value="mf" className="mt-4">
              {renderPayslipAccordion('mf')}
            </TabsContent>
          </Tabs>
        </DialogContent>
      </Dialog>

      {/* Announcements Modal */}
      <Dialog open={isAnnouncementModalOpen} onOpenChange={setIsAnnouncementModalOpen}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-2xl font-bold text-gray-900 flex items-center">
              <Megaphone className="h-6 w-6 text-indigo-600 mr-2" />
              Announcements!
            </DialogTitle>
            <DialogDescription>
               Latest updates and news from the team.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 mt-4">
            {announcements.map((announcement) => (
              <Card key={announcement.id} className="bg-white border-l-4 border-l-indigo-500 shadow-sm">
                <CardHeader className="pb-2">
                  <CardTitle className="text-lg font-semibold text-gray-800">
                    {announcement.title}
                  </CardTitle>
                  <p className="text-xs text-gray-500">
                    Valid until: {new Date(announcement.duration).toLocaleDateString()}
                  </p>
                </CardHeader>
                <CardContent>
                  {announcement.image_url && (
                    <div className="mb-4">
                      <img 
                        src={announcement.image_url} 
                        alt={announcement.title} 
                        className="w-full h-auto rounded-md"
                      />
                    </div>
                  )}
                  <p className="text-gray-700 whitespace-pre-wrap">
                    {announcement.body}
                  </p>
                </CardContent>
              </Card>
            ))}
          </div>

          <div className="flex justify-end mt-6">
            <Button onClick={() => setIsAnnouncementModalOpen(false)}>
              Close
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default EmployeeDashboard;
